import { Calendar, ChevronDown, ChevronRight, Download, Eye, FileText, Search } from 'lucide-react';
import { useEffect, useState } from 'react';
import { RoleBadge } from '../components/RoleBadge';
import { useAuth } from '../contexts/AuthContext';
import { adminApi } from '../lib/admin-api';
import { exportAuditLogs } from '../lib/export-utils';

/**
 * Audit log entry interface
 */
interface AuditLogEntry {
	id: string;
	timestamp: Date;
	actorEmail: string;
	actorName: string;
	actorRole: string;
	targetEmail: string;
	targetName: string;
	action: string;
	description: string;
	beforeData?: any;
	afterData?: any;
	metadata?: {
		ipAddress?: string;
		userAgent?: string;
		reason?: string;
		riskLevel?: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
	};
	success: boolean;
}

/**
 * Audit log detail modal component
 */
function AuditLogDetailModal({
	log,
	isOpen,
	onClose,
}: {
	log: AuditLogEntry | null;
	isOpen: boolean;
	onClose: () => void;
}) {
	if (!isOpen || !log) return null;

	return (
		<div className="fixed inset-0 z-50 overflow-y-auto">
			<div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
				<div className="fixed inset-0 transition-opacity" onClick={onClose}>
					<div className="absolute inset-0 bg-gray-500 opacity-75"></div>
				</div>

				<div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
					<div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
						<div className="flex items-center justify-between mb-4">
							<h3 className="text-lg font-medium text-gray-900 dark:text-white">
								Audit Log Details
							</h3>
							<button
								onClick={onClose}
								className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
							>
								<span className="sr-only">Close</span>✕
							</button>
						</div>

						<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
							{/* Basic Information */}
							<div className="space-y-4">
								<div>
									<label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
										Action
									</label>
									<p className="mt-1 text-sm text-gray-900 dark:text-white">
										{log.action}
									</p>
								</div>

								<div>
									<label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
										Description
									</label>
									<p className="mt-1 text-sm text-gray-900 dark:text-white">
										{log.description}
									</p>
								</div>

								<div>
									<label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
										Timestamp
									</label>
									<p className="mt-1 text-sm text-gray-900 dark:text-white">
										{new Date(log.timestamp).toLocaleString()}
									</p>
								</div>

								<div>
									<label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
										Status
									</label>
									<span
										className={`mt-1 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
											log.success
												? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
												: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
										}`}
									>
										{log.success ? 'Success' : 'Failed'}
									</span>
								</div>
							</div>

							{/* User Information */}
							<div className="space-y-4">
								<div>
									<label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
										Performed By
									</label>
									<div className="mt-1 flex items-center space-x-2">
										<span className="text-sm text-gray-900 dark:text-white">
											{log.actorName}
										</span>
										<span className="text-sm text-gray-500 dark:text-gray-400">
											({log.actorEmail})
										</span>
										<RoleBadge
											role={log.actorRole}
											size="xs"
											showTooltip={false}
										/>
									</div>
								</div>

								<div>
									<label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
										Target User
									</label>
									<div className="mt-1">
										<span className="text-sm text-gray-900 dark:text-white">
											{log.targetName}
										</span>
										<span className="text-sm text-gray-500 dark:text-gray-400 ml-2">
											({log.targetEmail})
										</span>
									</div>
								</div>

								{log.metadata?.riskLevel && (
									<div>
										<label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
											Risk Level
										</label>
										<span
											className={`mt-1 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
												log.metadata.riskLevel === 'LOW'
													? 'bg-green-100 text-green-800'
													: log.metadata.riskLevel === 'MEDIUM'
													? 'bg-yellow-100 text-yellow-800'
													: log.metadata.riskLevel === 'HIGH'
													? 'bg-orange-100 text-orange-800'
													: 'bg-red-100 text-red-800'
											}`}
										>
											{log.metadata.riskLevel}
										</span>
									</div>
								)}

								{log.metadata?.ipAddress && (
									<div>
										<label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
											IP Address
										</label>
										<p className="mt-1 text-sm text-gray-900 dark:text-white font-mono">
											{log.metadata.ipAddress}
										</p>
									</div>
								)}
							</div>
						</div>

						{/* Data Changes */}
						{log.beforeData && log.afterData && (
							<div className="mt-6">
								<h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
									Data Changes
								</h4>
								<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
									<div>
										<label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
											Before
										</label>
										<pre className="text-xs bg-gray-100 dark:bg-gray-900 p-3 rounded-md overflow-auto max-h-32">
											{JSON.stringify(log.beforeData, null, 2)}
										</pre>
									</div>
									<div>
										<label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
											After
										</label>
										<pre className="text-xs bg-gray-100 dark:bg-gray-900 p-3 rounded-md overflow-auto max-h-32">
											{JSON.stringify(log.afterData, null, 2)}
										</pre>
									</div>
								</div>
							</div>
						)}
					</div>

					<div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
						<button
							onClick={onClose}
							className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 sm:ml-3 sm:w-auto sm:text-sm"
						>
							Close
						</button>
					</div>
				</div>
			</div>
		</div>
	);
}

/**
 * Audit log table row component
 */
function AuditLogRow({
	log,
	onViewDetails,
}: {
	log: AuditLogEntry;
	onViewDetails: (log: AuditLogEntry) => void;
}) {
	const [expanded, setExpanded] = useState(false);

	const getSeverityColor = (riskLevel?: string) => {
		switch (riskLevel) {
			case 'LOW':
				return 'text-green-600 bg-green-100 dark:bg-green-900 dark:text-green-200';
			case 'MEDIUM':
				return 'text-yellow-600 bg-yellow-100 dark:bg-yellow-900 dark:text-yellow-200';
			case 'HIGH':
				return 'text-orange-600 bg-orange-100 dark:bg-orange-900 dark:text-orange-200';
			case 'CRITICAL':
				return 'text-red-600 bg-red-100 dark:bg-red-900 dark:text-red-200';
			default:
				return 'text-gray-600 bg-gray-100 dark:bg-gray-900 dark:text-gray-200';
		}
	};

	return (
		<>
			<tr className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
				<td className="px-6 py-4 whitespace-nowrap">
					<button
						onClick={() => setExpanded(!expanded)}
						className="flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
					>
						{expanded ? (
							<ChevronDown className="w-4 h-4" />
						) : (
							<ChevronRight className="w-4 h-4" />
						)}
					</button>
				</td>

				<td className="px-6 py-4 whitespace-nowrap">
					<div className="text-sm text-gray-900 dark:text-white">
						{new Date(log.timestamp).toLocaleDateString()}
					</div>
					<div className="text-xs text-gray-500 dark:text-gray-400">
						{new Date(log.timestamp).toLocaleTimeString()}
					</div>
				</td>

				<td className="px-6 py-4 whitespace-nowrap">
					<div className="flex items-center space-x-2">
						<span className="text-sm text-gray-900 dark:text-white">
							{log.actorName}
						</span>
						<RoleBadge role={log.actorRole} size="xs" showTooltip={false} />
					</div>
					<div className="text-xs text-gray-500 dark:text-gray-400">{log.actorEmail}</div>
				</td>

				<td className="px-6 py-4 whitespace-nowrap">
					<span className="text-sm font-medium text-gray-900 dark:text-white">
						{log.action}
					</span>
				</td>

				<td className="px-6 py-4">
					<div className="text-sm text-gray-900 dark:text-white max-w-xs truncate">
						{log.description}
					</div>
				</td>

				<td className="px-6 py-4 whitespace-nowrap">
					{log.metadata?.riskLevel && (
						<span
							className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getSeverityColor(
								log.metadata.riskLevel
							)}`}
						>
							{log.metadata.riskLevel}
						</span>
					)}
				</td>

				<td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
					<button
						onClick={() => onViewDetails(log)}
						className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
					>
						<Eye className="w-4 h-4" />
					</button>
				</td>
			</tr>

			{expanded && (
				<tr>
					<td colSpan={7} className="px-6 py-4 bg-gray-50 dark:bg-gray-900">
						<div className="space-y-3">
							<div>
								<span className="text-sm font-medium text-gray-700 dark:text-gray-300">
									Target:{' '}
								</span>
								<span className="text-sm text-gray-900 dark:text-white">
									{log.targetName} ({log.targetEmail})
								</span>
							</div>

							{log.metadata?.reason && (
								<div>
									<span className="text-sm font-medium text-gray-700 dark:text-gray-300">
										Reason:{' '}
									</span>
									<span className="text-sm text-gray-900 dark:text-white">
										{log.metadata.reason}
									</span>
								</div>
							)}

							{log.metadata?.ipAddress && (
								<div>
									<span className="text-sm font-medium text-gray-700 dark:text-gray-300">
										IP Address:{' '}
									</span>
									<span className="text-sm text-gray-900 dark:text-white font-mono">
										{log.metadata.ipAddress}
									</span>
								</div>
							)}
						</div>
					</td>
				</tr>
			)}
		</>
	);
}

/**
 * Audit Logs Page - Comprehensive audit trail viewer
 */
export function AuditLogsPage() {
	const { user } = useAuth();
	const [searchTerm, setSearchTerm] = useState('');
	const [actionFilter, setActionFilter] = useState<string>('all');
	const [riskFilter, setRiskFilter] = useState<string>('all');
	const [dateRange, setDateRange] = useState<'1d' | '7d' | '30d' | '90d'>('7d');
	const [selectedLog, setSelectedLog] = useState<AuditLogEntry | null>(null);
	const [showDetailModal, setShowDetailModal] = useState(false);
	const [auditData, setAuditData] = useState<{
		logs: AuditLogEntry[];
		total: number;
	} | null>(null);
	const [loading, setLoading] = useState(true);

	// Load audit logs from API with duplicate call prevention
	useEffect(() => {
		let isCancelled = false;

		const loadAuditLogs = async () => {
			if (isCancelled) return;

			try {
				setLoading(true);
				const data = await adminApi.getAuditLogsDetailed({
					action: actionFilter !== 'all' ? actionFilter : undefined,
					riskLevel: riskFilter !== 'all' ? riskFilter : undefined,
					dateRange,
				});

				if (!isCancelled) {
					setAuditData(data);
				}
			} catch (error) {
				if (!isCancelled) {
					console.error('Failed to load audit logs:', error);
					// Fallback to empty data
					setAuditData({
						logs: [],
						total: 0,
					});
				}
			} finally {
				if (!isCancelled) {
					setLoading(false);
				}
			}
		};

		loadAuditLogs();

		// Cleanup function to prevent state updates if component unmounts
		return () => {
			isCancelled = true;
		};
	}, [actionFilter, riskFilter, dateRange]);

	// Use real audit data or fallback to empty array
	const allLogs = auditData?.logs || [];

	const filteredLogs = allLogs.filter(log => {
		const matchesSearch =
			!searchTerm ||
			log.actorEmail.toLowerCase().includes(searchTerm.toLowerCase()) ||
			log.targetEmail.toLowerCase().includes(searchTerm.toLowerCase()) ||
			log.description.toLowerCase().includes(searchTerm.toLowerCase());

		// Action and risk filtering is handled by the API, but we can do client-side search
		return matchesSearch;
	});

	const handleViewDetails = (log: AuditLogEntry) => {
		setSelectedLog(log);
		setShowDetailModal(true);
	};

	const handleCloseModal = () => {
		setShowDetailModal(false);
		setSelectedLog(null);
	};

	const actionTypes = [
		'ROLE_CHANGE',
		'LOGIN',
		'FAILED_LOGIN',
		'WORKFLOW_DELETE',
		'USER_DEACTIVATE',
	];
	const riskLevels = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'];

	return (
		<div className="p-8 max-w-7xl mx-auto">
			{/* Header */}
			<div className="mb-8">
				<div className="flex items-center justify-between">
					<div>
						<h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center">
							<FileText className="mr-3 w-8 h-8 text-blue-600" />
							Audit Logs
						</h1>
						<p className="mt-2 text-gray-600 dark:text-gray-400">
							Complete audit trail of all system activities and security events
						</p>
					</div>

					<div className="flex items-center space-x-3">
						<button
							onClick={() => exportAuditLogs(filteredLogs)}
							className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:hover:bg-gray-600"
						>
							<Download className="mr-2 w-4 h-4" />
							Export CSV
						</button>
						<button className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:hover:bg-gray-600">
							<Calendar className="mr-2 w-4 h-4" />
							Date Range
						</button>
					</div>
				</div>
			</div>

			{/* Filters */}
			<div className="bg-white dark:bg-gray-800 shadow rounded-lg mb-6">
				<div className="p-6">
					<div className="grid grid-cols-1 md:grid-cols-4 gap-4">
						{/* Search */}
						<div className="relative">
							<div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
								<Search className="h-5 w-5 text-gray-400" />
							</div>
							<input
								type="text"
								placeholder="Search audit logs..."
								value={searchTerm}
								onChange={e => setSearchTerm(e.target.value)}
								className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white dark:bg-gray-700 dark:border-gray-600 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
							/>
						</div>

						{/* Action Filter */}
						<div>
							<select
								value={actionFilter}
								onChange={e => setActionFilter(e.target.value)}
								className="block w-full px-3 py-2 border border-gray-300 rounded-md bg-white dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
							>
								<option value="all">All Actions</option>
								{actionTypes.map(action => (
									<option key={action} value={action}>
										{action.replace('_', ' ')}
									</option>
								))}
							</select>
						</div>

						{/* Risk Filter */}
						<div>
							<select
								value={riskFilter}
								onChange={e => setRiskFilter(e.target.value)}
								className="block w-full px-3 py-2 border border-gray-300 rounded-md bg-white dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
							>
								<option value="all">All Risk Levels</option>
								{riskLevels.map(level => (
									<option key={level} value={level}>
										{level}
									</option>
								))}
							</select>
						</div>

						{/* Date Range */}
						<div>
							<select
								value={dateRange}
								onChange={e => setDateRange(e.target.value as any)}
								className="block w-full px-3 py-2 border border-gray-300 rounded-md bg-white dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
							>
								<option value="1d">Last 24 Hours</option>
								<option value="7d">Last 7 Days</option>
								<option value="30d">Last 30 Days</option>
								<option value="90d">Last 90 Days</option>
							</select>
						</div>
					</div>
				</div>
			</div>

			{/* Audit Logs Table */}
			<div className="bg-white dark:bg-gray-800 shadow overflow-hidden sm:rounded-md">
				<div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
					<h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
						Audit Trail ({filteredLogs.length} entries)
					</h3>
				</div>

				{filteredLogs.length === 0 ? (
					<div className="text-center py-12">
						<FileText className="mx-auto h-12 w-12 text-gray-400" />
						<h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
							No audit logs found
						</h3>
						<p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
							Try adjusting your search or filters.
						</p>
					</div>
				) : (
					<div className="overflow-x-auto">
						<table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
							<thead className="bg-gray-50 dark:bg-gray-900">
								<tr>
									<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
										{/* Expand column */}
									</th>
									<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
										Timestamp
									</th>
									<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
										Actor
									</th>
									<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
										Action
									</th>
									<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
										Description
									</th>
									<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
										Risk Level
									</th>
									<th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
										Actions
									</th>
								</tr>
							</thead>
							<tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
								{filteredLogs.map(log => (
									<AuditLogRow
										key={log.id}
										log={log}
										onViewDetails={handleViewDetails}
									/>
								))}
							</tbody>
						</table>
					</div>
				)}
			</div>

			{/* Audit Log Detail Modal */}
			<AuditLogDetailModal
				log={selectedLog}
				isOpen={showDetailModal}
				onClose={handleCloseModal}
			/>
		</div>
	);
}
